import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCustomerAuth } from '@/context/CustomerAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Lands here after Emergent Google OAuth.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Reads `session_id` from the URL fragment, exchanges it with our backend,
 * sets the customer-session cookie, then redirects to /account (or whatever
 * `?next=` indicates).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useCustomerAuth();
  const processed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');
    const search = new URLSearchParams(location.search || '');
    const next = search.get('next') || '/account';

    if (!sessionId) {
      setError('Sign-in failed: missing session id from Google.');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
      return;
    }

    (async () => {
      try {
        await axios.post(
          `${API}/customers/auth/session`,
          { session_id: sessionId },
          { withCredentials: true },
        );
        await refresh();
        navigate(next, { replace: true });
      } catch (err) {
        setError(err.response?.data?.detail || 'Sign-in failed. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    })();
  }, [location, navigate, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-callback-page">
      <div className="text-center">
        {error ? (
          <p className="text-red-700 text-sm" data-testid="auth-callback-error">{error}</p>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">Completing sign-in&hellip;</p>
          </>
        )}
      </div>
    </div>
  );
}
